import { useEffect } from "react";
import type { Vistoria } from "@/types/vistorias";
import { estadoFitoVistoriaConfig, riscoVistoriaConfig } from "@/config/vistorias-config";
import InspectionStatusBadge from "./InspectionStatusBadge";
import InspectionTypeBadge from "./InspectionTypeBadge";

interface InspectionDetailDrawerProps {
  vistoria: Vistoria | null;
  onClose: () => void;
  onAction: (action: string, v: Vistoria) => void;
}

export default function InspectionDetailDrawer({ vistoria, onClose, onAction }: InspectionDetailDrawerProps) {
  useEffect(() => {
    if (vistoria) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [vistoria]);

  if (!vistoria) return null;

  const fitoConfig = estadoFitoVistoriaConfig[vistoria.estadoFitossanitario] || { badge: "", label: vistoria.estadoFitossanitario };
  const riscoConfig = riscoVistoriaConfig[vistoria.classeRisco] || { badge: "", label: vistoria.classeRisco };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Overlay */}
      <div className="absolute inset-0 bg-foreground-900/30" onClick={onClose}></div>

      {/* Drawer */}
      <div className="relative w-full max-w-[520px] bg-white h-full overflow-y-auto shadow-xl animate-slide-in-right">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-background-200/70 px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-bold text-foreground-900 font-heading">Detalhe da Vistoria</h2>
            <p className="text-xs text-foreground-400 font-mono">{vistoria.id}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer transition-colors">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Tree identity */}
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center shrink-0">
              <i className="ri-plant-line text-primary-600 text-xl"></i>
            </div>
            <div className="min-w-0">
              <p className="font-mono text-xs text-accent-600 font-medium">{vistoria.codigoCta}</p>
              <h3 className="text-lg font-bold text-foreground-900 font-heading">{vistoria.nomePopular}</h3>
              <p className="text-xs text-foreground-400 italic">{vistoria.especie}</p>
            </div>
          </div>

          {/* Badges row */}
          <div className="flex flex-wrap items-center gap-2">
            <InspectionStatusBadge status={vistoria.status} />
            <InspectionTypeBadge type={vistoria.tipo} />
            <span className={`badge ${fitoConfig.badge}`}>{fitoConfig.label}</span>
            <span className={`badge ${riscoConfig.badge}`}>{riscoConfig.label}</span>
          </div>

          {/* Basic info grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-background-50 rounded-lg p-3 border border-background-200/50">
              <span className="text-xs text-foreground-400">Data da Vistoria</span>
              <p className="text-sm font-semibold text-foreground-800">{new Date(vistoria.data + "T00:00:00").toLocaleDateString("pt-BR")}</p>
            </div>
            <div className="bg-background-50 rounded-lg p-3 border border-background-200/50">
              <span className="text-xs text-foreground-400">Técnico</span>
              <p className="text-sm font-semibold text-foreground-800">{vistoria.tecnico}</p>
            </div>
            <div className="bg-background-50 rounded-lg p-3 border border-background-200/50">
              <span className="text-xs text-foreground-400">Projeto</span>
              <p className="text-sm font-medium text-foreground-700 leading-snug">{vistoria.projeto}</p>
            </div>
            <div className="bg-background-50 rounded-lg p-3 border border-background-200/50">
              <span className="text-xs text-foreground-400">Localização</span>
              <p className="text-sm font-medium text-foreground-700 leading-snug">{vistoria.logradouro} — {vistoria.bairro}</p>
            </div>
          </div>

          {/* Dendrometrics */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Dados Dendrométricos</h4>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-background-50 rounded-lg p-3 border border-background-200/50 text-center">
                <span className="text-2xl font-bold text-foreground-900 font-heading">{vistoria.dap}</span>
                <span className="block text-xs text-foreground-400">DAP (cm)</span>
              </div>
              <div className="bg-background-50 rounded-lg p-3 border border-background-200/50 text-center">
                <span className="text-2xl font-bold text-foreground-900 font-heading">{vistoria.altura}</span>
                <span className="block text-xs text-foreground-400">Altura (m)</span>
              </div>
              <div className="bg-background-50 rounded-lg p-3 border border-background-200/50 text-center">
                <span className="text-2xl font-bold text-foreground-900 font-heading">{vistoria.diametroCopa}</span>
                <span className="block text-xs text-foreground-400">Copa (m)</span>
              </div>
            </div>
          </div>

          {/* Symptoms */}
          {vistoria.sintomas.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Sintomas Observados</h4>
              <div className="flex flex-wrap gap-1.5">
                {vistoria.sintomas.map((s) => (
                  <span key={s} className="badge bg-warning-50 text-warning-700 border border-warning-200/60">{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* Interferences */}
          {vistoria.interferencias.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Interferências</h4>
              <div className="flex flex-wrap gap-1.5">
                {vistoria.interferencias.map((inf) => (
                  <span key={inf} className="badge bg-accent-50 text-accent-700 border border-accent-200/60">{inf}</span>
                ))}
              </div>
            </div>
          )}

          {/* Recommendation */}
          <div className="bg-caution-50/60 rounded-lg p-3 border border-caution-200/40">
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">Recomendação de Manejo</h4>
            <p className="text-sm font-semibold text-caution-800">{vistoria.recomendacao}</p>
          </div>

          {/* Technical opinion */}
          {vistoria.parecer && (
            <div>
              <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Parecer Técnico</h4>
              <div className="bg-background-50 rounded-lg p-3 border border-background-200/50">
                <p className="text-sm text-foreground-700 leading-relaxed">{vistoria.parecer}</p>
              </div>
            </div>
          )}

          {/* Photos */}
          <div>
            <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-2">Fotos da Vistoria ({vistoria.fotos})</h4>
            <div className="grid grid-cols-3 gap-2">
              {Array.from({ length: Math.min(vistoria.fotos, 3) }).map((_, i) => (
                <div key={i} className="aspect-square rounded-lg bg-background-100 border border-background-200/60 flex items-center justify-center">
                  <i className="ri-image-line text-foreground-300 text-xl"></i>
                </div>
              ))}
            </div>
          </div>

          {/* Coordinates */}
          <div className="flex items-center justify-between text-xs text-foreground-500 bg-background-50 rounded-lg p-3 border border-background-200/50">
            <span className="font-mono">{vistoria.coordenada}</span>
            <button className="text-accent-600 font-medium hover:text-accent-700 cursor-pointer whitespace-nowrap">
              <i className="ri-file-copy-line mr-1"></i>Copiar coordenadas
            </button>
          </div>
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-white border-t border-background-200/70 px-5 py-4 flex items-center gap-2 flex-wrap">
          <button onClick={() => onAction("validate", vistoria)} className="btn-primary text-xs">
            <i className="ri-check-double-line"></i>Validar
          </button>
          <button onClick={() => onAction("edit", vistoria)} className="btn-secondary text-xs">
            <i className="ri-edit-line"></i>Editar
          </button>
          <button onClick={() => onAction("report", vistoria)} className="btn-outline text-xs">
            <i className="ri-file-text-line"></i>Gerar Parecer
          </button>
          <button onClick={() => onAction("fca", vistoria)} className="btn-outline text-xs">
            <i className="ri-plant-line"></i>Abrir FCA
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.25s ease-out;
        }
      `}</style>
    </div>
  );
}